import { FC, ReactNode } from 'react';
import './index.css';

type Product = {
  icon: string;
  iconClass: string;
  tag: string;
  tagClass: string;
  title: string;
  href?: string;
  desc: string;
  features: string[];
  decoration: ReactNode;
};

const navLinks = [
  { href: '#products', label: 'Products' },
  { href: '#achievements', label: 'Track Record' },
  { href: '#team', label: 'Team' },
  { href: '#press', label: 'Press' },
  { href: '#careers', label: 'Careers' },
];

const heroStats = [
  { value: '$60M+', label: 'Total Funding Raised' },
  { value: '$500M', label: 'Peak Valuation' },
  { value: '$1B+', label: 'Peak TVL' },
  { value: '2', label: 'Product Lines' },
];

const products: Product[] = [
  {
    icon: '🔱',
    iconClass: 'infra',
    tag: 'Live · Mainnet',
    tagClass: 'live',
    title: 'Manta Network',
    href: 'https://manta.network/',
    desc: 'A modular Ethereum Layer 2 built for low-cost, high-throughput transactions. Originally pioneered zero-knowledge privacy on Polkadot, now consolidated as Manta Pacific — an EVM-compatible rollup with Celestia DA and fast finality via Symbiotic restaking.',
    features: ['Ethereum L2', 'Celestia DA', 'ZK Heritage', 'Fast Finality', 'EVM Compatible'],
    decoration: (
      <svg className="card-illus" viewBox="0 0 140 140" fill="none">
        <path
          d="M70 10 C 35 10, 5 40, 20 70 C 28 88, 45 100, 58 108 C 65 112, 70 125, 70 135 C 70 125, 75 112, 82 108 C 95 100, 112 88, 120 70 C 135 40, 105 10, 70 10Z"
          fill="#00b894"
          opacity="0.15"
        />
      </svg>
    ),
  },
  {
    icon: '⚡',
    iconClass: 'ai',
    tag: 'Building · 2026',
    tagClass: 'building',
    title: 'Agentese',
    href: 'https://www.agentese.ai/',
    desc: "AI agent lab building your personal workforce — autonomous agents that handle meetings, hiring, code security, and daily routines so you don't have to. Each agent owns one job end-to-end. More agent shipping…",
    features: ['Agent Lab', 'Personal AI Workforce'],
    decoration: (
      <svg className="card-illus" viewBox="0 0 140 140" fill="none">
        <circle cx="70" cy="70" r="50" stroke="#4a6cf7" strokeWidth="2" opacity="0.12" />
        <circle
          cx="70"
          cy="70"
          r="30"
          stroke="#4a6cf7"
          strokeWidth="1.5"
          opacity="0.08"
          strokeDasharray="4 3"
        />
        <circle cx="70" cy="40" r="5" fill="#4a6cf7" opacity="0.2" />
        <circle cx="95" cy="85" r="5" fill="#00b894" opacity="0.2" />
        <circle cx="45" cy="85" r="5" fill="#a855f7" opacity="0.15" />
      </svg>
    ),
  },
  {
    icon: '🧭',
    iconClass: 'ai',
    tag: 'Building · 2026',
    tagClass: 'building',
    title: 'Yarrow',
    href: 'https://yarrowlab.ai/',
    desc: 'AI policy simulation platform — input a policy question, get a calibrated probability with reasoning chain, scenario distribution, and stakeholder impact map. Multiple AI analysts independently reason from curated evidence packs through a multi-gate audit pipeline. Built for governments, sovereign wealth funds, and institutional decision-makers.',
    features: ['Policy Simulation', 'Strategic Foresight', 'Multi-Agent', 'Enterprise'],
    decoration: (
      <svg className="card-illus" viewBox="0 0 140 140" fill="none">
        <path d="M70 120 L70 55" stroke="#4a6cf7" strokeWidth="2" opacity="0.12" />
        <path
          d="M70 80 L40 45 M70 70 L100 40 M70 55 L70 25"
          stroke="#4a6cf7"
          strokeWidth="1.5"
          opacity="0.1"
        />
        <circle cx="40" cy="45" r="6" fill="#4a6cf7" opacity="0.15" />
        <circle cx="100" cy="40" r="6" fill="#00b894" opacity="0.15" />
        <circle cx="70" cy="25" r="6" fill="#a855f7" opacity="0.15" />
      </svg>
    ),
  },
  {
    icon: '🧪',
    iconClass: 'labs',
    tag: 'Live · Incubator',
    tagClass: 'live',
    title: 'Manta Labs',
    href: 'https://labs.manta.network/',
    desc: 'A chain-agnostic application incubator launching consumer-facing products across multiple blockchains. First portfolio project SUPERFORTUNE reached #1 DAU on BNB Chain for AI dApps.',
    features: ['App Incubation', 'Multi-chain', 'Consumer Apps'],
    decoration: (
      <svg className="card-illus" viewBox="0 0 140 140" fill="none">
        <rect x="25" y="25" rx="20" width="90" height="90" fill="#a855f7" opacity="0.06" />
        <circle cx="55" cy="60" r="8" fill="#a855f7" opacity="0.1" />
        <circle cx="85" cy="60" r="8" fill="#a855f7" opacity="0.1" />
        <circle cx="70" cy="85" r="8" fill="#a855f7" opacity="0.1" />
      </svg>
    ),
  },
  {
    icon: '🔗',
    iconClass: 'convergence',
    tag: 'Research · 2026',
    tagClass: 'research',
    title: 'AI × Crypto Settlement',
    desc: "When AI agents operate at scale — booking services, processing payments, coordinating logistics — they generate massive micro-transactions. We're building infrastructure to settle these on Manta Pacific.",
    features: ['Micro-payments', 'Agent Wallets', 'Low-cost Settlement'],
    decoration: (
      <svg className="card-illus" viewBox="0 0 140 140" fill="none">
        <path
          d="M30 70 Q70 20 110 70 Q70 120 30 70Z"
          stroke="#ff6b6b"
          strokeWidth="1.5"
          fill="#ff6b6b"
          opacity="0.06"
        />
        <circle cx="70" cy="70" r="6" fill="#ff6b6b" opacity="0.15" />
      </svg>
    ),
  },
];

const achievements = [
  '$1B+|Peak TVL across Manta Pacific, one of the largest L2s by value locked',
  '200K+|Daily transactions processed on Manta Pacific at peak activity',
  '300K+|Zero-knowledge soulbound tokens (zkSBT) minted on the network',
  '30+|Top-tier investors including Polychain, Binance Labs, Qiming, CoinFund',
  '2020|Founded as one of the earliest ZK-focused projects in the industry',
  '#1|SUPERFORTUNE reached #1 AI dApp by DAU on BNB Chain (Oct 2025)',
].map(item => {
  const [num, label] = item.split('|');
  return { num, label };
});

const teamMembers = [
  {
    avatar: '/images/team/victor.jpg',
    initials: 'VJ',
    name: 'Victor Ji',
    role: 'Co-founder & CEO',
    bio: 'Harvard Kennedy School. Previously venture capital and infrastructure investment in Israel. Co-chaired Harvard Blockchain & Cryptocurrency Club. Angel investor in SOON, Redpill AI, and others.',
  },
  {
    avatar: '/images/team/kenny.jpg',
    initials: 'KL',
    name: 'Kenny Li',
    role: 'Co-founder & COO',
    bio: 'MIT Sloan School of Management. Leads operations and business development. Previously involved in institutional blockchain infrastructure and DeFi product management.',
  },
];

const investors = [
  'Polychain Capital',
  'Binance Labs',
  'Qiming Venture Partners',
  'CoinFund',
  'Alliance',
  'SevenX Ventures',
  'ParaFi Capital',
  'Multicoin Capital',
  'Spartan Group',
  'DeFiance Capital',
  'Hypersphere Ventures',
];

const pressItems = [
  {
    source: 'ABMedia',
    title: 'Manta 究竟不只是 L2？創辦人大談專案發展，或成 Solana 與以太坊橋樑',
    date: 'December 2024',
    href: 'https://abmedia.io/manta-victor-ji',
  },
  {
    source: 'BlockTempo',
    title: '對話 Manta 創辦人：我們為何離開波卡與 OP Stack？',
    date: 'December 2023',
    href: 'https://www.blocktempo.com/manta-founder-why-should-we-leave-polkadot-and-op-stack/',
  },
  {
    source: 'TechCrunch',
    title: 'Team behind crypto privacy protocol Manta Network is now valued at $500M',
    date: 'July 2023',
    href: 'https://techcrunch.com/2023/07/19/team-behind-crypto-privacy-protocol-manta-network-is-now-valued-at-500m/',
  },
  {
    source: 'The Block',
    title: 'Manta Network developer p0x labs raises $25 million for Layer 2 ZK apps',
    date: 'July 2023',
    href: 'https://www.theblock.co/post/240481/manta-network-p0x-labs-raises-25-million-layer-2-zk-apps',
  },
];

export const Index: FC = () => {
  return (
    <div id="top" className="p0x-page">
      <nav>
        <a href="#top" className="nav-logo">
          <span>p0x</span>eidon labs
        </a>
        <div className="nav-links">
          {navLinks.map(link => (
            <a key={link.href} href={link.href}>
              {link.label}
            </a>
          ))}
        </div>
      </nav>

      <section className="hero">
        <div className="hero-blob" />
        <div className="hero-illus hero-illus-1">
          <svg viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M200 40 C 100 40, 20 120, 60 200 C 80 250, 120 280, 160 300 C 180 310, 200 340, 200 370 C 200 340, 220 310, 240 300 C 280 280, 320 250, 340 200 C 380 120, 300 40, 200 40Z"
              fill="url(#hg)"
              opacity="0.6"
            />
            <circle cx="160" cy="160" r="6" fill="#00b894" opacity="0.7" />
            <circle cx="240" cy="160" r="6" fill="#4a6cf7" opacity="0.7" />
            <circle cx="200" cy="200" r="8" fill="#00b894" opacity="0.9" />
            <circle cx="200" cy="120" r="4" fill="#4a6cf7" opacity="0.5" />
            <line
              x1="160"
              y1="160"
              x2="200"
              y2="200"
              stroke="#00b894"
              strokeWidth="1.5"
              opacity="0.3"
            />
            <line
              x1="240"
              y1="160"
              x2="200"
              y2="200"
              stroke="#4a6cf7"
              strokeWidth="1.5"
              opacity="0.3"
            />
            <line
              x1="200"
              y1="120"
              x2="200"
              y2="200"
              stroke="#00b894"
              strokeWidth="1"
              opacity="0.2"
            />
            <line
              x1="160"
              y1="160"
              x2="200"
              y2="120"
              stroke="#4a6cf7"
              strokeWidth="1"
              opacity="0.2"
            />
            <line
              x1="240"
              y1="160"
              x2="200"
              y2="120"
              stroke="#00b894"
              strokeWidth="1"
              opacity="0.2"
            />
            <defs>
              <linearGradient id="hg" x1="60" y1="40" x2="340" y2="370">
                <stop offset="0%" stopColor="#00b894" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#4a6cf7" stopOpacity="0.15" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="hero-illus hero-illus-2">
          <svg viewBox="0 0 180 180" fill="none">
            <rect x="20" y="20" width="140" height="140" rx="32" fill="#4a6cf7" opacity="0.06" />
            <circle cx="90" cy="90" r="30" stroke="#4a6cf7" strokeWidth="2" opacity="0.15" />
            <circle
              cx="90"
              cy="90"
              r="50"
              stroke="#00b894"
              strokeWidth="1"
              opacity="0.1"
              strokeDasharray="6 4"
            />
            <circle cx="90" cy="60" r="4" fill="#4a6cf7" opacity="0.4" />
            <circle cx="116" cy="105" r="4" fill="#00b894" opacity="0.4" />
            <circle cx="64" cy="105" r="4" fill="#a855f7" opacity="0.3" />
          </svg>
        </div>
        <div className="hero-illus hero-illus-3">
          <svg viewBox="0 0 100 100" fill="none">
            <polygon
              points="50,10 90,75 10,75"
              stroke="#00b894"
              strokeWidth="1.5"
              fill="#00b894"
              opacity="0.06"
            />
            <circle cx="50" cy="50" r="5" fill="#00b894" opacity="0.3" />
          </svg>
        </div>

        <div className="hero-content">
          <div className="hero-badge">
            <span className="dot" /> Singapore-based · Founded 2020
          </div>
          <h1>
            Web3 infrastructure
            <br />& <span className="accent-text">real-world</span>
            <br />
            <span className="accent-text">AI agents</span>
          </h1>
          <p className="hero-desc">
            p0xeidon labs builds blockchain infrastructure for privacy-preserving transactions and
            AI agents that interact with the physical world.
          </p>
          <div className="hero-stats">
            {heroStats.map(stat => (
              <div key={stat.label} className="stat-item">
                <div className="stat-value mono">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="products">
        <div className="section-label">Products</div>
        <h2 className="section-title">Two verticals. One mission.</h2>
        <p className="section-desc">
          Technology at the intersection of cryptography, blockchain, and artificial intelligence —
          connecting digital systems to real-world actions.
        </p>
        <div className="products-grid">
          {products.map(product =>
            product.href !== undefined ? (
              <a
                key={product.title}
                href={product.href}
                target="_blank"
                rel="noopener noreferrer"
                className="product-card product-card-link"
              >
                {product.decoration}
                <div className={`product-icon ${product.iconClass}`}>{product.icon}</div>
                <span className={`tag ${product.tagClass}`}>{product.tag}</span>
                <h3>{product.title}</h3>
                <p>{product.desc}</p>
                <div className="product-features">
                  {product.features.map(feature => (
                    <span key={feature} className="feature-pill">
                      {feature}
                    </span>
                  ))}
                </div>
              </a>
            ) : (
              <div key={product.title} className="product-card">
                {product.decoration}
                <div className={`product-icon ${product.iconClass}`}>{product.icon}</div>
                <span className={`tag ${product.tagClass}`}>{product.tag}</span>
                <h3>{product.title}</h3>
                <p>{product.desc}</p>
                <div className="product-features">
                  {product.features.map(feature => (
                    <span key={feature} className="feature-pill">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </section>

      <section id="achievements">
        <div className="section-label">Track Record</div>
        <h2 className="section-title">What we've built and shipped</h2>
        <p className="section-desc">
          From zero-knowledge cryptography on Polkadot to a top Ethereum L2 — production blockchain
          infrastructure since 2020.
        </p>
        <div className="achievements-grid">
          {achievements.map(item => (
            <div key={item.num + item.label} className="achievement-card">
              <div className="num">{item.num}</div>
              <div className="label">{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="team">
        <div className="section-label">Leadership</div>
        <h2 className="section-title">Founded by builders</h2>
        <p className="section-desc">
          Deep expertise in cryptography, systems engineering, and finance from Harvard, MIT, UC
          Santa Barbara, and Algorand.
        </p>
        <div className="team-grid">
          {teamMembers.map(member => (
            <div key={member.name} className="team-card">
              <div className="team-avatar">
                <img src={member.avatar} alt={member.name} />
              </div>
              <h4>{member.name}</h4>
              <div className="role mono">{member.role}</div>
              <div className="bio">{member.bio}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="investors">
        <div className="section-label">Investors & Partners</div>
        <h2 className="section-title">Backed by leading investors</h2>
        <p className="section-desc">
          $60M+ raised across multiple rounds from the industry's most respected funds at a peak
          valuation of $500M.
        </p>
        <div className="investor-logos">
          {investors.map(investor => (
            <div key={investor} className="investor-pill">
              {investor}
            </div>
          ))}
        </div>
      </section>

      <section id="press">
        <div className="section-label">Press</div>
        <h2 className="section-title">In the news</h2>
        <div className="section-desc" />
        <div className="press-grid">
          {pressItems.map(item => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="press-card"
            >
              <div className="press-source">{item.source}</div>
              <div className="press-title">{item.title}</div>
              <div className="press-date">{item.date}</div>
            </a>
          ))}
        </div>
      </section>

      <section id="careers">
        <div className="section-label">Careers</div>
        <h2 className="section-title">Join us</h2>
        <p className="section-desc">
          Building an AI-native team in Singapore. If you're excited about the intersection of AI
          agents and crypto infrastructure, we want to hear from you.
        </p>
        <a
          href="https://www.notion.so/mantanetwork/Manta-Hiring-JD-15b1945b2708423f927e6cb9508750f3"
          target="_blank"
          rel="noopener noreferrer"
          className="career-cta"
        >
          View Open Positions →
        </a>
      </section>

      <footer>
        <div className="footer-left">
          <div className="footer-logo">
            <span>p0x</span>eidon labs
          </div>
          <div className="footer-address">
            9 Raffles Place #06-01
            <br />
            Republic Plaza, Singapore 048619
            <br />
            <br />
            P0XEIDON LABS PTE. LTD.
            <br />
            <br />
            contact@p0xlabs.xyz
          </div>
        </div>
        <div className="footer-links">
          <div className="footer-col">
            <h5>Products</h5>
            <a href="https://manta.network/" target="_blank" rel="noopener noreferrer">
              Manta Network
            </a>
            <a
              href="https://www.agentese.ai/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Agentese"
            >
              Agentese
            </a>
            <a
              href="https://labs.manta.network/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Manta Labs"
            >
              Manta Labs
            </a>
          </div>
          <div className="footer-col">
            <h5>Resources</h5>
            <a href="https://docs.manta.network" target="_blank" rel="noopener noreferrer">
              Documentation
            </a>
            <a href="/" aria-label="Blog">
              Blog
            </a>
            <a href="/" aria-label="Brand Kit">
              Brand Kit
            </a>
          </div>
          <div className="footer-col">
            <h5>Connect</h5>
            <a href="https://twitter.com/MantaNetwork" target="_blank" rel="noopener noreferrer">
              Twitter / X
            </a>
            <a href="https://discord.gg/mantanetwork" target="_blank" rel="noopener noreferrer">
              Discord
            </a>
            <a href="mailto:contact@p0xlabs.xyz">Email</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
